import { GameState } from '../../../types/game';
import { capturePreviousKeptSegment } from '../../../domain/segmentsCapture';
import { computeScore } from '../../../domain/scoring';
import { allKept, canRescueByFive } from '../../../domain/dice';
import { makeEmptyDiceMask } from '../../../domain/gameRules';
import { computeStatusAfterRoll } from '../../../domain/turnCycleStateMachine';

export interface RollComputationInput {
  state: GameState;
  dice: number[];
  keptMask: boolean[];
  rollIndex: number;
  eventId?: string;
  isHotDiceReset?: boolean;
  rolledDice?: number[];
  rolledIndices?: number[];
  rollMeta?: { points: number; scoringDiceCount: number; sum: number; allScoring: boolean; rescueAvailable: boolean; bust: boolean; didBust: boolean };
}

export interface RollComputationResult {
  segments: NonNullable<GameState['segments']>;
  capturedMask: boolean[];
  lastRoll: NonNullable<NonNullable<GameState['turnCycle']>['lastRoll']>;
  status: NonNullable<GameState['turnCycle']>['status'];
  derivedDice: number[];
  derivedIndices: number[];
  meta: NonNullable<RollComputationInput['rollMeta']>;
  diceOrigins: number[];
}

// Jedno źródło prawdy dla obliczeń meta rzutu/kept/capture.
export function computeRollState(input: RollComputationInput): RollComputationResult {
  const {
    state,
    dice,
    keptMask,
    rollIndex,
    eventId,
    isHotDiceReset = false,
    rolledDice,
    rolledIndices,
    rollMeta,
  } = input;

  const captureResult = capturePreviousKeptSegment(state);
  const segments = captureResult.segments;
  let capturedMask = captureResult.capturedMask;

  if (isHotDiceReset) {
    capturedMask = makeEmptyDiceMask();
  }

  const prevOrigins = state.turnCycle?.diceOrigins || [];
  const derivedIndices = rolledIndices ?? dice.map((_, idx) => idx).filter((idx) => !keptMask[idx]);
  const derivedDice = rolledDice ?? derivedIndices.map((idx) => dice[idx]);
  const diceOrigins = dice.map((_, idx) => {
    if (!keptMask[idx]) return rollIndex;
    return typeof prevOrigins[idx] === 'number' ? prevOrigins[idx] : rollIndex;
  });

  let meta = rollMeta;
  if (!meta) {
    const sum = derivedDice.reduce((s, v) => s + v, 0);
    const sc = derivedDice.length
      ? computeScore(derivedDice)
      : { basePoints: 0, scoringDiceCount: 0, breakdown: [], breakdownDetails: [] };
    const bust = derivedDice.length > 0 && sc.scoringDiceCount === 0;
    const rescueEnabled = state.settings?.rescueByFive !== undefined ? state.settings.rescueByFive : true;
    const rescueCandidate = bust && rescueEnabled && canRescueByFive(derivedDice, sc.scoringDiceCount || 0);
    meta = {
      points: sc.basePoints || 0,
      scoringDiceCount: sc.scoringDiceCount || 0,
      sum,
      allScoring: derivedDice.length > 0 && sc.scoringDiceCount === derivedDice.length,
      rescueAvailable: rescueCandidate,
      bust,
      didBust: bust && !rescueCandidate,
    };
  }

  const lastRoll = {
    dice: derivedDice,
    indices: derivedIndices,
    points: meta.points,
    scoringDiceCount: meta.scoringDiceCount,
    sum: meta.sum,
    allScoring: meta.allScoring,
    rescueAvailable: meta.rescueAvailable,
    bust: meta.bust,
    didBust: meta.didBust,
    eventId,
    isHotDiceReset,
    rollIndex,
  };

  const status = computeStatusAfterRoll(meta.didBust, allKept(keptMask));

  return {
    segments,
    capturedMask,
    lastRoll,
    status,
    derivedDice,
    derivedIndices,
    meta,
    diceOrigins,
  };
}
